import type { ReactNode } from 'react'
import { Link } from 'react-router'
import type { InlineText } from './types'

/** `code`, **bold** and [label](href). Anything else is plain text. */
const TOKEN = /(`[^`]+`|\*\*[^*]+\*\*|\[[^\]]+\]\([^)\s]+\))/g

const linkClass =
  'font-medium text-teal underline decoration-teal/30 underline-offset-2 hover:decoration-teal'

function renderToken(token: string, key: number): ReactNode {
  if (token.startsWith('`')) {
    return (
      <code
        key={key}
        className="rounded-xs bg-wash px-1 py-px font-mono text-[0.86em] text-ink-soft break-words"
      >
        {token.slice(1, -1)}
      </code>
    )
  }
  if (token.startsWith('**')) {
    return (
      <strong key={key} className="font-semibold text-ink">
        {token.slice(2, -2)}
      </strong>
    )
  }
  const match = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(token)
  if (!match) return token
  const [, label, href] = match as unknown as [string, string, string]
  if (href.startsWith('/') || href.startsWith('#')) {
    return (
      <Link key={key} to={href} className={linkClass}>
        {label}
      </Link>
    )
  }
  return (
    <a key={key} href={href} className={linkClass} target="_blank" rel="noreferrer">
      {label}
    </a>
  )
}

/** Render the handbook's inline syntax (see types.ts). */
export function Inline({ text }: { text: InlineText }) {
  const parts = text.split(TOKEN)
  return <>{parts.map((part, index) => (index % 2 === 1 ? renderToken(part, index) : part))}</>
}

/** Plain text of an inline string, for search. */
export function plainText(text: InlineText): string {
  return text
    .replace(/\[([^\]]+)\]\([^)\s]+\)/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
}
